import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';

const toRoman = (n) => ['I','II','III','IV','V','VI','VII','VIII','IX','X'][n-1] || n;

/**
 * Modal allowing the user to choose a spell slot level to cast a spell at.
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible.
 * @param {function} props.onHide - Callback when the modal is closed.
 * @param {number} props.baseLevel - Minimum level of the spell.
 * @param {Object} props.slots - Mapping of { regular: {level: count}, warlock: {level: count} }.
 * @param {Object} [props.used] - Mapping of used slots keyed by `${type}-${level}`.
 * @param {function} props.onSelect - Callback invoked with the chosen level and slot type.
  * @param {string} [props.higherLevels] - Description of benefits when upcasting.
 */
export default function UpcastModal({
  show,
  onHide,
  baseLevel = 1,
  slots = { regular: {}, warlock: {} },
  used = {},
  onSelect,
  higherLevels,
  proficiencyAction,
  title = 'Cast at Level',
  describeLevel,
  compact = false,
  children,
}) {
  const regularLevels = Object.keys(slots.regular || {})
    .map(Number)
    .filter((lvl) => lvl >= baseLevel && slots.regular[lvl] > 0)
    .sort((a, b) => a - b);
  const warlockLevels = Object.keys(slots.warlock || {})
    .map(Number)
    .filter((lvl) => lvl >= baseLevel && slots.warlock[lvl] > 0)
    .sort((a, b) => a - b);

  const firstRegular = regularLevels[0];
  const firstWarlock = warlockLevels[0];
  const initial =
    typeof firstRegular === 'number'
      ? { level: firstRegular, type: 'regular' }
      : typeof firstWarlock === 'number'
      ? { level: firstWarlock, type: 'warlock' }
      : { level: null, type: null };

  const [selection, setSelection] = useState(initial);

  useEffect(() => {
    setSelection(initial);
  }, [initial.level, initial.type, show]);

  const [casting, setCasting] = useState(false);
  const [castError, setCastError] = useState('');
  const castingRef = React.useRef(false);
  useEffect(() => { if (show) setCastError(''); }, [show]);
  const commit = async callback => {
    if (castingRef.current) return;
    castingRef.current = true; setCasting(true); setCastError('');
    try {
      const result = await callback?.();
      if (result !== false) onHide?.();
    } catch (error) { setCastError(error.message || 'Unable to cast spell.'); }
    finally { castingRef.current = false; setCasting(false); }
  };
  const handleConfirm = () => selection.level && commit(() => onSelect?.(selection.level, selection.type));
  const handleProficiencyClick = proficiencyAction?.onClick ? () => commit(proficiencyAction.onClick) : null;

  return (
    <Modal
      show={show}
      onHide={casting ? undefined : onHide}
      centered
      className={compact ? 'upcast-modal upcast-modal--compact' : 'upcast-modal'}
      dialogClassName={compact ? 'upcast-modal__dialog' : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {castError && <p role="alert" className="text-danger">{castError}</p>}
        {children}
        {proficiencyAction && (
          <div className="d-flex flex-column align-items-center mb-3">
            <Button
              className="upcast-proficiency-button"
              size="sm"
              aria-label={
                proficiencyAction.ariaLabel || 'cast using proficiency feature'
              }
              onClick={handleProficiencyClick || undefined}
              disabled={
                casting || proficiencyAction.disabled ||
                typeof handleProficiencyClick !== 'function'
              }
            >
              {proficiencyAction.icon || proficiencyAction.label || 'P'}
            </Button>
            {proficiencyAction.remainingText && (
              <small className="text-muted mt-1">
                {proficiencyAction.remainingText}
              </small>
            )}
          </div>
        )}
        {higherLevels && (
          <p className="text-muted mb-2">{higherLevels}</p>
        )}
        {regularLevels.length > 0 && (
          <div className={compact ? 'upcast-choice-grid mb-2' : 'mb-2 d-flex flex-wrap gap-2 justify-content-center'}>
            {regularLevels.map((lvl) => {
              const key = `regular-${lvl}`;
              const usedSlots = used[key] || {};
              const usedCount = Object.values(usedSlots).filter(Boolean).length;
              const total = slots.labels?.[lvl] ? 0 : (slots.regular?.[lvl] || 0) + usedCount;
              return (
                <button
                  type="button"
                  key={key}
                  className={`spell-slot upcast-slot${
                    selection.type === 'regular' && selection.level === lvl
                      ? ' selected'
                      : ''
                  }`}
                  onClick={() => setSelection({ level: lvl, type: 'regular' })}
                  aria-pressed={selection.type === 'regular' && selection.level === lvl}
                >
                  <div className="slot-level">{compact ? `Level ${lvl}` : toRoman(lvl)}</div>
                  {describeLevel && <span className="upcast-slot__effect">{describeLevel(lvl)}</span>}
                  {(compact || slots.labels?.[lvl]) && <span className="upcast-slot__pool">{slots.labels?.[lvl] || 'Spellcasting'}</span>}
                  <div className="slot-boxes">
                    {Array.from({ length: total }).map((_, i) => (
                      <div
                        key={i}
                        className={`slot-small ${
                          usedSlots[i] ? 'slot-used' : 'slot-active'
                        }`}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {warlockLevels.length > 0 && (
          <div className={compact ? 'upcast-choice-grid' : 'd-flex flex-wrap gap-2 justify-content-center'}>
            {warlockLevels.map((lvl) => {
              const key = `warlock-${lvl}`;
              const usedSlots = used[key] || {};
              const usedCount = Object.values(usedSlots).filter(Boolean).length;
              const total = (slots.warlock?.[lvl] || 0) + usedCount;
              return (
                <button
                  type="button"
                  key={key}
                  className={`spell-slot upcast-slot warlock-slot${
                    selection.type === 'warlock' && selection.level === lvl
                      ? ' selected'
                      : ''
                  }`}
                  onClick={() => setSelection({ level: lvl, type: 'warlock' })}
                  aria-pressed={selection.type === 'warlock' && selection.level === lvl}
                >
                  <div className="slot-level">{compact ? `Level ${lvl}` : toRoman(lvl)}</div>
                  {describeLevel && <span className="upcast-slot__effect">{describeLevel(lvl)}</span>}
                  {compact && <span className="upcast-slot__pool">Pact Magic</span>}
                  <div className="slot-boxes">
                    {Array.from({ length: total }).map((_, i) => (
                      <div
                        key={i}
                        className={`slot-small ${
                          usedSlots[i] ? 'slot-used' : 'slot-active'
                        }`}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {regularLevels.length === 0 && warlockLevels.length === 0 && (
          <p>No spell slots of this level available.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={casting} onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={casting || !selection.level}
        >
          Cast
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

